/* eslint-disable sonarjs/no-identical-functions */
import React, { useEffect, useMemo } from 'react'
import {
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CInputGroup,
  CFormInput,
  CFormCheck,
  CFormSelect,
} from '@coreui/react-pro'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { CandidateListFilterOptionsProps } from '../../../types/Recruitment/CandidateList/CandidateListTypes'
import { reduxServices } from '../../../reducers/reduxServices'
import { useTypedSelector } from '../../../stateStore'
import ModifiedDatePicker from '../../../components/ReusableComponent/ModifiedDatePicker'
import { DateIsBefore, FormatDateHelper } from '../../../utils/dateFormatUtils'
import { openToDateHandler } from '../../../utils/datePicketUtils'
import { dateFormat } from '../../../constant/DateFormat'
import {
  createNumberArray,
  listComposer,
  selectDateOptions,
  StringEmpty,
} from '../../../utils/helper'
import { OSelectDynamic } from '../../../components/ReusableComponent/OSelectList'
import { OAutoCompleteDynamic } from '../../../components/ReusableComponent/OAutoComplete'
import { OInputFieldDynamic } from '../../../components/ReusableComponent/OInputField'
import { returnValidatedDecimal } from '../../../constant/constantData'
import { getJobCode } from '../../../types/Recruitment/InterviewerConfiguration/interviewerConfigurationTypes'
import OToast from '../../../components/ReusableComponent/OToast'
import './CandidateListFilterOptions'

const CandidateListFilterOptions = ({
  searchKeyDownButtonHandler,
  clearButtonHandler,
  searchButtonHandler,
  userAccess,
  filterBody,
  setFilterBody,
  handleExportCandidate,
  setDateError,
  dateError,
}: CandidateListFilterOptionsProps): JSX.Element => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isVendor = localStorage.getItem('isVendor') === 'true'
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const jobCodeParam = queryParams.get('jobCode')
  const vendorName = queryParams.get('vendorName')
  const interviewRoundOptions = [0, ...createNumberArray(5)]
  const getAllEmpCountries = useTypedSelector(
    reduxServices.candidateList.selectors.getAllEmpCountries,
  )
  const getAllTechnology = useTypedSelector(
    reduxServices.candidateList.selectors.getAllTechnology,
  )
  const getAllStatus = useTypedSelector(
    reduxServices.candidateList.selectors.getAllStatus,
  )
  const allEmployeesList = useTypedSelector(
    (state) => state.candidateList.allEmployeeDetailsList,
  )
  //const vendorsList = useTypedSelector((state) => state.vendorList.vendors)
  const sortedTechnologyDetails = useMemo(() => {
    if (Array.isArray(getAllTechnology)) {
      const sortTemp = getAllTechnology
        .slice()
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
      return listComposer(sortTemp as [], 'id', 'name')
    }
    return []
  }, [getAllTechnology])

  const vendorsList = useTypedSelector(
    reduxServices.resumeMatcher.selectors.selectVendorsList,
  )

  const [jobVacancies, setJobVacancies] = React.useState<getJobCode[]>([])
  const [, setLoadingJobs] = React.useState<boolean>(false)
  const [selectedJobCode, setSelectedJobCode] = React.useState<string>(
    filterBody.appliedFor || '',
  )

  const jobCodesFromStore = useTypedSelector(
    reduxServices.interviewerConfiguration.selectors.jobCodes,
  )

  useEffect(() => {
    dispatch(reduxServices.candidateList.getStatus())
    dispatch(
      reduxServices.vendorList.getVendors({
        startIndex: 0,
        endIndex: 1000,
        vendorName: '',
        isHrVendor: true,
      }),
    )
    dispatch(reduxServices.resumeMatcher.getExternalVendorsList())
  }, [dispatch])

  useEffect(() => {
    const fetchJobs = async () => {
      setLoadingJobs(true)
      try {
        await dispatch(reduxServices.interviewerConfiguration.getAllJobCodes())
      } catch (error) {
        dispatch(
          reduxServices.app.actions.addToast(
            <OToast
              toastColor="label-danger"
              toastMessage="Failed to load job vacancies"
            />,
          ),
        )
      } finally {
        setLoadingJobs(false)
      }
    }
    fetchJobs()
  }, [dispatch])

  useEffect(() => {
    if (jobCodesFromStore && jobCodesFromStore.length > 0) {
      setJobVacancies(jobCodesFromStore)
    }
  }, [jobCodesFromStore])

  // Track if radio button has been clicked to avoid triggering on initial load
  const [isResumeFilterChanged, setIsResumeFilterChanged] =
    React.useState(false)

  const [recruiterType, setRecruiterType] = React.useState<string>('All')
  const [isRecruiterTypeChanged, setIsRecruiterTypeChanged] =
    React.useState(false)

  const [isJobCodeFromUrl, setIsJobCodeFromUrl] = React.useState(false)
  const [isVendorNameFromUrl, setIsVendorNameFromUrl] = React.useState(false)
  const hasInitializedRef = React.useRef(false)
  const hasVendorInitializedRef = React.useRef(false)

  useEffect(() => {
    if (jobCodeParam && !hasInitializedRef.current) {
      hasInitializedRef.current = true
      setSelectedJobCode(jobCodeParam)
      setFilterBody((prev) => ({
        ...prev,
        appliedFor: jobCodeParam,
      }))
      //setTimeout(() => {
      setIsJobCodeFromUrl(true)
      // }, 0)
    }
  }, [jobCodeParam, setFilterBody])

  useEffect(() => {
    if (vendorName && !hasVendorInitializedRef.current) {
      hasVendorInitializedRef.current = true
      setRecruiterType('External')
      setFilterBody((prev) => ({
        ...prev,
        isExternalCreated: true,
        recruiter: vendorName,
      }))
      setTimeout(() => {
        setIsVendorNameFromUrl(true)
      }, 0)
    }
  }, [vendorName, setFilterBody])

  useEffect(() => {
    if (filterBody.appliedFor && filterBody.appliedFor !== selectedJobCode) {
      setSelectedJobCode(filterBody.appliedFor)
    }
  }, [filterBody.appliedFor, selectedJobCode])

  useEffect(() => {
    const { isExternalCreated } = filterBody
    if (isExternalCreated === false) {
      setRecruiterType('Internal')
    } else if (isExternalCreated === true) {
      setRecruiterType('External')
    } else {
      setRecruiterType('All')
    }
  }, [filterBody.isExternalCreated])

  useEffect(() => {
    if (isResumeFilterChanged) {
      searchButtonHandler({} as React.MouseEvent<HTMLButtonElement>)
      setIsResumeFilterChanged(false)
    }
  }, [filterBody.isResumeMatcher, isResumeFilterChanged, searchButtonHandler])

  useEffect(() => {
    if (isRecruiterTypeChanged) {
      searchButtonHandler({} as React.MouseEvent<HTMLButtonElement>)
      setIsRecruiterTypeChanged(false)
    }
  }, [
    filterBody.isExternalCreated,
    isRecruiterTypeChanged,
    searchButtonHandler,
  ])

  useEffect(() => {
    if (isJobCodeFromUrl) {
      searchButtonHandler({} as React.MouseEvent<HTMLButtonElement>)
      setIsJobCodeFromUrl(false)
    }
  }, [isJobCodeFromUrl, searchButtonHandler])

  useEffect(() => {
    if (isVendorNameFromUrl) {
      searchButtonHandler({} as React.MouseEvent<HTMLButtonElement>)
      setIsVendorNameFromUrl(false)
    }
  }, [isVendorNameFromUrl, searchButtonHandler])

  const composedCountries = useMemo(() => {
    return listComposer(getAllEmpCountries as [], 'id', 'name')
  }, [getAllEmpCountries])

  const composedVendors = useMemo(() => {
    if (!Array.isArray(vendorsList)) return []

    return vendorsList.map((vendor, index) => ({ id: index, name: vendor }))
  }, [vendorsList])

  type StatusLike =
    | {
        id?: string | number
        name?: string
        status?: string
        statusName?: string
        statusId?: string | number
      }
    | string
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const sortedStatusDetails = useMemo(() => {
    const result: { [key: string]: string } = {}
    result['All'] = 'All'
    if (!Array.isArray(getAllStatus) || getAllStatus.length === 0) {
      return result
    }

    const vendorExcludedStatuses = ['JOINED', 'DID_NOT_JOIN', 'OFFER_CANCELLED']

    ;(getAllStatus.slice() as StatusLike[])
      .map((s) => {
        if (typeof s === 'object' && s !== null) {
          const name = s.name ?? s.status ?? s.statusName ?? ''
          const id = s.id ?? s.statusId ?? name
          return { id: String(id), name }
        }
        const primitiveName = String(s ?? '')
        return { id: primitiveName, name: primitiveName }
      })
      .filter((x) => x.name !== '')
      // Filter out specific statuses for vendors
      .filter((x) => {
        if (isVendor) {
          const normalizedName = String(x.name)
            .trim()
            .toUpperCase()
            .replace(/[_\s-]/g, '_')
          return !vendorExcludedStatuses.includes(normalizedName)
        }
        return true
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((x) => {
        const nameStr = String(x.name).trim()
        let displayName = ''
        // Hyphenate statuses that start with L<number> (e.g., L2_SHORTLISTED -> L2-SHORTLISTED)
        if (/^L\d+[_-]/i.test(nameStr)) {
          const parts = nameStr.split(/[_-]+/)
          const first = parts.shift() || ''
          displayName = (
            first + (parts.length ? '-' + parts.join('-') : '')
          ).toUpperCase()
        } else {
          const cleaned = nameStr
            .replace(/[_-]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
          displayName = cleaned.toUpperCase()
          // Special-case: show 'IN PROCESS' as 'IN PROGRESS'
          if (displayName === 'IN PROCESS') {
            displayName = 'IN PROGRESS'
          }
        }
        result[x.id] = displayName
      })
    return result
  }, [getAllStatus, isVendor])

  const composedDateOptions = listComposer(
    selectDateOptions as [],
    'label',
    'name',
  )

  const employeeRole = useTypedSelector(
    reduxServices.authentication.selectors.selectEmployeeRole,
  )

  const filterOnChangeHandler = (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target
    if (name === 'selectionStatus' && value !== 'Custom') {
      setFilterBody((prev) => ({
        ...prev,
        [name]: value,
        fromDate: '',
        toDate: '',
      }))
    } else if (name === 'experience') {
      setFilterBody((prev) => ({
        ...prev,
        [name]: returnValidatedDecimal(value, filterBody.experience),
      }))
    } else if (name === 'interviewRound') {
      setFilterBody((prev) => ({
        ...prev,
        [name]: value === '' ? '' : Number(value),
      }))
    } else {
      setFilterBody((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const recruiterTypeChangeHandler = (
    event:
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = event.target
    setRecruiterType(value)
    let isExternalCreatedValue: string | boolean = 'All'
    if (value === 'Internal') {
      isExternalCreatedValue = false
    } else if (value === 'External') {
      isExternalCreatedValue = true
    }
    setFilterBody((prev) => ({
      ...prev,
      recruiter: '',
      isExternalCreated: isExternalCreatedValue,
    }))
    setIsRecruiterTypeChanged(true)
  }

  const handleJobCodeChange = (value: string) => {
    setSelectedJobCode(value)
    setFilterBody((prev) => ({
      ...prev,
      appliedFor: value,
    }))
  }

  const fromDateChangeHandler = (date: Date) => {
    // converts the dateString into a preferred format 'dd/mm/yyyy'
    const istFormatted = FormatDateHelper(date)
    // assigns the value istFormatted into a state.
    setFilterBody((prev) => ({
      ...prev,
      fromDate: istFormatted,
    }))
  }

  const toDateChangeHandler = (date: Date) => {
    const istFormatted = FormatDateHelper(date)
    setFilterBody((prev) => ({
      ...prev,
      toDate: istFormatted,
    })) // dd/mm/yyyy format
  }

  useEffect(() => {
    if (filterBody.selectionStatus === 'Custom') {
      const isBefore = DateIsBefore(
        filterBody.toDate,
        filterBody.fromDate,
        dateFormat,
        {
          inclusivity: false,
        },
      )
      setDateError(isBefore)
    } else {
      setFilterBody((prev) => ({
        ...prev,
        toDate: '',
        fromDate: '',
      }))
    }
  }, [filterBody.fromDate, filterBody.toDate, dateFormat])

  const enableViewButton = useMemo(() => {
    const dateCustomFlag = () => {
      if (filterBody.selectionStatus === 'Custom') {
        return (
          StringEmpty(filterBody.fromDate) || StringEmpty(filterBody.toDate)
        )
      }
      return false
    }

    return dateCustomFlag() || dateError
  }, [dateError, filterBody])

  return (
    <div className="candidate-filter-options">
      <CRow
        className="g-3 filter-row"
        xs={{ cols: 1 }}
        md={{ cols: 1 }}
        xl={{ cols: 2 }}
      >
        <CCol xs={{ span: 12, order: 1 }} xl={{ span: 10, order: 0 }}>
          <CRow
            className="g-3 align-items-center"
            sm={{ cols: 1 }}
            md={{ gutterX: 1 }}
          >
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="filter-label"
              selectClassName="pe-2"
              name="candidateStatus"
              label="Status"
              ariaLabel="Default select example"
              value={filterBody.candidateStatus}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Status'}
              list={sortedStatusDetails}
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="filter-label"
              selectClassName="pe-2"
              name="selectionTechnology"
              label="Technology"
              ariaLabel="Default select example"
              list={sortedTechnologyDetails}
              value={filterBody.selectionTechnology}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Technology'}
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="filter-label"
              selectClassName="pe-2"
              name="selectionCountry"
              label="Country"
              ariaLabel="Default select example"
              list={composedCountries}
              useIdValueOptions={true}
              value={String(filterBody.selectionCountry)}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Country'}
            />
          </CRow>
          <CRow
            className="g-3 mt-md-1 align-items-center"
            sm={{ cols: 1 }}
            lg={{ cols: 3, gutterY: 3, gutterX: 1 }}
          >
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="filter-label"
              selectClassName="pe-2"
              name="selectionStatus"
              label="Date"
              ariaLabel="Default select example"
              list={composedDateOptions}
              value={filterBody.selectionStatus as string}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Date'}
            />
            <OInputFieldDynamic
              inputMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              inputColumnType={{ sm: 8 }}
              autoComplete="off"
              labelClassName="filter-label"
              inputClassName="pe-2"
              name="experience"
              label="Experience"
              value={filterBody.experience}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Experience'}
            />
            <OSelectDynamic
              selectMainColType={{ md: 4 }}
              labelColumnType={{ sm: 4 }}
              selectColumnType={{ sm: 8 }}
              labelClassName="filter-label"
              selectClassName="pe-2"
              name="interviewRound"
              label="Interview Round"
              ariaLabel="Default select example"
              list={interviewRoundOptions.map((value) => (
                <option key={value} value={value}>
                  {`L${value}`}
                </option>
              ))}
              value={String(filterBody.interviewRound)}
              setValueHandler={filterOnChangeHandler}
              placeHolder={'Select Round'}
            />
          </CRow>
          {isVendor && (
            <CRow
              className="g-3 mt-md-1 align-items-center"
              sm={{ cols: 1 }}
              lg={{ cols: 3, gutterY: 3, gutterX: 1 }}
            >
              <CCol md={4}>
                <CRow>
                  <CCol sm={4} className="text-md-end">
                    <CFormLabel className="mt-1">Applied For:</CFormLabel>
                  </CCol>
                  <CCol sm={8}>
                    <select
                      className="form-select form-select-sm pe-2"
                      aria-label="Job Title select"
                      name="appliedFor"
                      value={selectedJobCode}
                      onChange={(e) => handleJobCodeChange(e.target.value)}
                    >
                      <option value="">Select Job Title</option>
                      {jobVacancies.map((vacancy) => (
                        <option key={vacancy.id} value={vacancy.jobCode}>
                          {vacancy.jobCode} - {vacancy.positionVacant}
                        </option>
                      ))}
                    </select>
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
          )}
          {!isVendor && (
            <CRow
              className="g-3 mt-md-1 align-items-center"
              sm={{ cols: 1 }}
              lg={{ cols: 3, gutterY: 3, gutterX: 1 }}
            >
              <OSelectDynamic
                selectMainColType={{ md: 4 }}
                labelColumnType={{ sm: 4 }}
                selectColumnType={{ sm: 8 }}
                labelClassName="filter-label"
                selectClassName="pe-2"
                name="recruiterType"
                label="Recruiter"
                ariaLabel="Select recruiter type"
                list={{
                  Internal: 'Internal',
                  External: 'External',
                }}
                value={recruiterType || 'Select recruiter'}
                setValueHandler={recruiterTypeChangeHandler}
                placeHolder="Select recruiter"
              />

              {recruiterType === 'Internal' && (
                <CCol md={4}>
                  <OAutoCompleteDynamic
                    labelColumnType={{ sm: 4 }}
                    selectColumnType={{ sm: 8 }}
                    items={allEmployeesList}
                    autocomplete="on"
                    placeholder="Recruiter Name"
                    label="Recruiter Name"
                    name="recruiter"
                    value={filterBody.recruiter}
                    setValueHandler={filterOnChangeHandler}
                    onSelect={(selectedVal) => {
                      setFilterBody((prev) => ({
                        ...prev,
                        recruiter: selectedVal,
                      }))
                    }}
                  />
                </CCol>
              )}
              {recruiterType === 'External' && (
                <OSelectDynamic
                  selectMainColType={{ md: 4 }}
                  labelColumnType={{ sm: 4 }}
                  selectColumnType={{ sm: 8 }}
                  labelClassName="filter-label"
                  selectClassName="pe-2"
                  name="recruiter"
                  label="Vendor Name"
                  ariaLabel="Select vendor"
                  list={composedVendors}
                  value={filterBody.recruiter}
                  setValueHandler={filterOnChangeHandler}
                  placeHolder="Select Vendor"
                />
              )}
              <CCol md={4}>
                <CRow>
                  <CCol sm={4} className="text-md-end">
                    <CFormLabel className="mt-1">Applied For:</CFormLabel>
                  </CCol>
                  <CCol sm={8}>
                    <CFormSelect
                      className="form-select form-select-sm w-100 text-truncate"
                      style={{
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                      aria-label="Job Title select"
                      name="appliedFor"
                      value={selectedJobCode}
                      onChange={(e) => handleJobCodeChange(e.target.value)}
                    >
                      <option value="">Select Job Title</option>
                      {jobVacancies.map((vacancy) => (
                        <option key={vacancy.id} value={vacancy.jobCode}>
                          {vacancy.jobCode} - {vacancy.positionVacant}
                        </option>
                      ))}
                    </CFormSelect>
                  </CCol>
                </CRow>
              </CCol>
            </CRow>
          )}
          {!isVendor && (
            <CRow className="mb-3 mt-4">
              <CCol xs={12}>
                <div className="d-inline ms-4">
                  {/* <CFormLabel className="me-3">Resume Filter:</CFormLabel> */}

                  <div className="d-inline">
                    <CFormCheck
                      type="radio"
                      name="resumeFilter"
                      id="allCandidates"
                      label="All"
                      value="all"
                      checked={
                        !filterBody.isResumeMatcher ||
                        filterBody.isResumeMatcher === ''
                      }
                      onChange={() => {
                        setFilterBody((prev) => ({
                          ...prev,
                          isResumeMatcher: '',
                        }))
                        setIsResumeFilterChanged(true)
                      }}
                      inline
                    />
                    <CFormCheck
                      type="radio"
                      name="resumeFilter"
                      id="resumeMatcher"
                      label="Resume Matcher"
                      value="matcher"
                      checked={filterBody.isResumeMatcher === 'true'}
                      onChange={() => {
                        setFilterBody((prev) => ({
                          ...prev,
                          isResumeMatcher: 'true',
                        }))
                        setIsResumeFilterChanged(true)
                      }}
                      inline
                    />
                    <CFormCheck
                      type="radio"
                      name="resumeFilter"
                      id="resumeParser"
                      label="Resume Parser"
                      value="parser"
                      checked={filterBody.isResumeMatcher === 'false'}
                      onChange={() => {
                        setFilterBody((prev) => ({
                          ...prev,
                          isResumeMatcher: 'false',
                        }))
                        setIsResumeFilterChanged(true)
                      }}
                      inline
                    />
                  </div>
                </div>
              </CCol>
            </CRow>
          )}
          {filterBody.selectionStatus === 'Custom' ? (
            <>
              <CRow className="mb-4 mt-3" xs={{ cols: 1 }} md={{ gutter: 1 }}>
                <CCol md={4}>
                  <CRow>
                    <CCol sm={4} className="text-md-end">
                      <CFormLabel className="mt-2">
                        From :
                        {StringEmpty(filterBody.fromDate) && (
                          <span className="text-danger">*</span>
                        )}
                      </CFormLabel>
                    </CCol>
                    <CCol sm={8}>
                      <ModifiedDatePicker
                        id="fromDate"
                        data-testid="candidateInterviewFromDate"
                        className="form-control form-control-sm sh-date-picker"
                        autoComplete="off"
                        dropdownMode="select"
                        showMonthDropdown
                        showYearDropdown
                        onChange={fromDateChangeHandler}
                        value={filterBody.fromDate}
                        openToDate={openToDateHandler(filterBody.fromDate)}
                        disabledKeyboardNavigation={true}
                        selected={filterBody.fromDate}
                      />
                    </CCol>
                  </CRow>
                </CCol>
                <CCol md={4}>
                  <CRow>
                    <CCol sm={4} className="text-md-end">
                      <CFormLabel className="mt-2">
                        To :
                        {StringEmpty(filterBody.toDate) && (
                          <span className="text-danger">*</span>
                        )}
                      </CFormLabel>
                    </CCol>
                    <CCol sm={8}>
                      <ModifiedDatePicker
                        id="toDate"
                        data-testid="ticketReportFromDate"
                        className="form-control form-control-sm sh-date-picker"
                        dropdownMode="select"
                        showMonthDropdown
                        autoComplete="off"
                        showYearDropdown
                        onChange={toDateChangeHandler}
                        value={filterBody.toDate}
                        openToDate={openToDateHandler(filterBody.toDate)}
                        disabledKeyboardNavigation={true}
                        selected={filterBody.toDate}
                      />
                    </CCol>
                  </CRow>
                </CCol>
                <CCol md={4}>
                  {dateError && (
                    <CRow className="ps-md-3">
                      <CCol md={10} xl={7}>
                        <span className="text-danger">
                          <b>To date should be greater than From date</b>
                        </span>
                      </CCol>
                    </CRow>
                  )}
                </CCol>
              </CRow>
            </>
          ) : (
            <></>
          )}
        </CCol>
      </CRow>
      <CRow
        className="filter-actions justify-content-center mt-4 mt-sm-5 pt-sm-2 pe-sm-5 me-sm-5"
        xs={{ cols: 2 }}
      >
        <CCol sm={'auto'} className="text-right">
          <CButton
            className="cursor-pointer"
            color="success btn-ovh me-1"
            onClick={(e) => {
              searchButtonHandler(e)
              ;(e.target as HTMLButtonElement).blur()
            }}
            data-testId="view-btn"
            disabled={enableViewButton}
          >
            View
          </CButton>
        </CCol>
        <CCol sm={'auto'}>
          <CButton
            className="cursor-pointer"
            disabled={false}
            data-testid="clear-btn"
            color="warning btn-ovh me-1"
            onClick={(e) => {
              clearButtonHandler(e)
              setRecruiterType('All')
              setSelectedJobCode('')
              ;(e.target as HTMLButtonElement).blur()
            }}
          >
            Clear
          </CButton>
        </CCol>
      </CRow>
      <CRow className="justify-content-end mt-4">
        <CCol className="text-end mb-1" md={'auto'} sm={'auto'} xs={9}>
          {employeeRole === 'Vendor' ? (
            <></>
          ) : (
            <>
              <CButton
                color="primary btn-ovh me-0 mt-md-0 mt-1"
                data-testId="candidate-interview-list-btn"
                onClick={() => {
                  dispatch(
                    reduxServices.app.actions.setReRouteLocation(
                      '/candidateInterviewList',
                    ),
                  )
                  history.push('/candidateInterviewList')
                }}
              >
                {/* <Link to="/candidateInterviewList" style={{ color: 'white' }}> */}
                <i className="fa fa-sign-out fa-fw  me-1" /> Candidate Interview
                List
                {/* </Link> */}
              </CButton>
            </>
          )}
        </CCol>
        <CCol className="text-end mb-1" md={'auto'} sm={'auto'} xs={9}>
          {userAccess?.createaccess && !isVendor && (
            <CButton
              color="primary btn-ovh me-0 mt-md-0 mt-1"
              data-testId="add-Candidate"
            >
              <Link to="/addNewCandidate" style={{ color: 'white' }}>
                <i className="fa fa-plus me-1"></i> Add Candidate
              </Link>
            </CButton>
          )}
        </CCol>
        <CCol className="text-end mb-1" md={'auto'} sm={'auto'} xs={9}>
          <CButton
            color="primary btn-ovh me-0 mt-md-0 mt-1"
            size="sm"
            onClick={handleExportCandidate}
            data-testId="export-Candidate"
          >
            <i className="fa fa-plus me-1"></i>
            Click to Export
          </CButton>
        </CCol>
      </CRow>

      <CRow className="justify-content-end mt-3">
        <CCol className="text-end" md={'auto'} sm={'auto'} xs={9}>
          <span className="text-danger" data-testid="errorMessage">
            <b> Note: Select a date to proceed with exporting the data.</b>
          </span>
        </CCol>
      </CRow>

      <CRow className="gap-2 d-md-flex justify-content-md-end mt-3 mb-3">
        <CCol sm={6} md={4} lg={5} xl={4} xxl={3}>
          <CInputGroup className="global-search me-0">
            <CFormInput
              id="searchByMultipleFlag"
              name="searchByMultipleFlag"
              data-testid="searchField"
              placeholder="Multiple Search"
              aria-label="Multiple Search"
              aria-describedby="button-addon2"
              value={filterBody.searchByMultipleFlag?.replace(/^\s*/, '')}
              autoComplete="off"
              onChange={filterOnChangeHandler}
              onKeyDown={searchKeyDownButtonHandler}
            />
            <CButton
              disabled={StringEmpty(
                filterBody.searchByMultipleFlag?.replace(/^\s*/, ''),
              )}
              data-testid="multi-search-btn"
              className="cursor-pointer"
              type="button"
              color="primary"
              id="button-addon2"
              onClick={searchButtonHandler}
            >
              <i className="fa fa-search"></i>
            </CButton>
          </CInputGroup>
        </CCol>
      </CRow>
    </div>
  )
}

export default CandidateListFilterOptions
